import { useEffect } from "react";
import { create } from "zustand";
import {
  DEFAULT_SEVERITY_COLOR_MAP,
  SEVERITY_DEFAULT_PALETTE_ID,
  fetchSeverityColorConfig,
  severityEntriesToMap,
  type SeverityColorMap,
} from "../api/frameworksApi";

/**
 * Global severity-colour state.
 *
 * Single source of truth for the five admin-configurable severity tiers
 * (critical / high / medium / low / notAvailable). Seeded with the shipped
 * default palette, hydrated once from the backend after login, updated live by
 * the Severity Colour Management page on Save / Reset, and read by
 * `itCompliance/colors.ts` so every VM / CM / CISO chart recolours.
 *
 * Zustand (not a React context) to match the app's store convention
 * (`toastStore`, `authStore`) and so non-component modules (`colors.ts`) can
 * read the current palette via `getState()`.
 */
interface SeverityColorState {
  colors: SeverityColorMap;
  activePaletteId: string;
  hydrated: boolean;
  hydrating: boolean;
  /** Replace the live palette immediately (used by the page's Save / Reset). */
  setColors: (colors: SeverityColorMap, activePaletteId?: string) => void;
  /**
   * Fetch the saved palette once per session. Keeps the current colours on
   * failure so the UI never renders colourless. Safe to call from many mount
   * points — guarded so the GET fires only once.
   */
  hydrate: () => Promise<void>;
}

export const useSeverityColorStore = create<SeverityColorState>((set, get) => ({
  colors: { ...DEFAULT_SEVERITY_COLOR_MAP },
  activePaletteId: SEVERITY_DEFAULT_PALETTE_ID,
  hydrated: false,
  hydrating: false,
  setColors: (colors, activePaletteId) =>
    set((prev) => ({ colors, activePaletteId: activePaletteId ?? prev.activePaletteId })),
  hydrate: async () => {
    if (get().hydrated || get().hydrating) return;
    set({ hydrating: true });
    try {
      const config = await fetchSeverityColorConfig();
      set({
        colors: severityEntriesToMap(config.colors ?? []),
        activePaletteId: config.activePaletteId ?? SEVERITY_DEFAULT_PALETTE_ID,
        hydrated: true,
      });
    } catch {
      // Keep the default palette on failure — mark hydrated so a broken
      // endpoint doesn't retry on every dashboard mount.
      set({ hydrated: true });
    } finally {
      set({ hydrating: false });
    }
  },
}));

/**
 * Subscribe a dashboard to the live palette and hydrate it once on mount.
 * Returns the current colour map. Calling this from a dashboard root
 * (IT Compliance, CISO Insights) makes its chart subtree re-render — and
 * therefore re-read `severityColor()` — the moment a saved palette resolves or
 * the admin saves new colours.
 */
export function useSeverityColorSync(): SeverityColorMap {
  const colors = useSeverityColorStore((s) => s.colors);
  const hydrate = useSeverityColorStore((s) => s.hydrate);
  useEffect(() => {
    void hydrate();
  }, [hydrate]);
  return colors;
}
